import { useEffect, useState } from "react"
import Script from "next/script"

import OrangeButton from "@src/components/Buttons/OrangeButton"
import LoadingSpinner from "@src/components/loading-spinner"

import type { UserCardOnFile } from "@src/entities/types/api"

export interface AccountPaymentMethodsProps {
  canDeleteMethod: boolean
  errorMessage: string | null
  getCardToken: (nonce: string) => void | Promise<void>
  isProcessing: boolean
  paymentMethod?: Partial<UserCardOnFile> | null
  onDeletePaymentMethod: React.MouseEventHandler<HTMLAnchorElement>
}
export default function AccountPaymentMethods({
  canDeleteMethod,
  getCardToken,
  errorMessage,
  paymentMethod,
  isProcessing,
  onDeletePaymentMethod,
}: AccountPaymentMethodsProps) {
  const [creditCard, setCreditCard] = useState<any>()
  const [isEditing, setIsEditing] = useState<boolean>(true)

  const _tokenize = async (method: any): Promise<string> => {
    const tokenResult = await method.tokenize()
    if (tokenResult.status !== "OK") {
      const errors = (tokenResult.errors ?? []).map(
        (error: any) => error.message
      )
      throw new Error(
        errors.length > 0
          ? `${errors.join(". ")}.`
          : "Something went wrong trying to validate your credit card. Please check your entry and try again."
      )
    }
    return tokenResult.token
  }

  async function attachCardToDiv() {
    await creditCard?.attach("#credit-card-form")
  }

  const beginUpdatingCard = async () => {
    try {
      const token = await _tokenize(creditCard)
      await getCardToken(token)
    } catch (error) {
      // console.log(error)
    }
  }

  useEffect(() => {
    if (!isProcessing && isEditing) {
      setIsEditing(false)
    }
  }, [isProcessing])

  useEffect(() => {
    if (creditCard) {
      attachCardToDiv()
    }
  }, [creditCard])

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-extrabold font-browser">
          Update Payment Method
        </h3>
      </div>{" "}
      {isProcessing && <LoadingSpinner size={24} />}
      {paymentMethod && (
        <p className="mb-2">
          Your saved payment method is{" "}
          <strong>
            {paymentMethod.cardType} {paymentMethod.lastFour} (
            {paymentMethod.exp})
          </strong>
          .{" "}
          {canDeleteMethod ? (
            <span style={{ opacity: isProcessing ? 0.25 : 1 }}>
              <a
                href="#"
                onClick={onDeletePaymentMethod}
                className="text-orange text-sm"
              >
                Remove this payment method
              </a>
              .
            </span>
          ) : (
            <span className="">
              Enter a new card below to update or change your payment method. To
              remove this card on file, you would first need to cancel your
              Ritual+ subscription.
            </span>
          )}
        </p>
      )}
      {paymentMethod === null && (
        <p
          dangerouslySetInnerHTML={{
            __html: "You do not have any saved payment methods on file.",
          }}
        />
      )}
      {errorMessage && (
        <p
          className="text-red text-sm mb-2"
          dangerouslySetInnerHTML={{
            __html: errorMessage,
          }}
        />
      )}
      <div id="credit-card-form" />
      <div className="mb-2">
        <OrangeButton
          disabled={isProcessing}
          onClick={() => {
            beginUpdatingCard()
          }}
          text="Update Card on File"
        />
      </div>
      <Script
        onLoad={async () => {
          // @ts-ignore
          const payments = window.Square.payments(
            process.env.NEXT_PUBLIC_SQUARE_APPLICATION_ID,
            process.env.NEXT_PUBLIC_SQUARE_LOCATION_ID
          )
          const card = await payments.card()
          setCreditCard(card)
        }}
        src={process.env.NEXT_PUBLIC_SQUARE_SCRIPT_URL}
      />
    </section>
  )
}
